import React, { useState, useEffect } from "react"
import {
    Container, Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input
} from "reactstrap"
import Select from "react-select"
import Datatables from "components/Datatables/datatable"
import { get, post } from "helpers/api_helper"
import toastr from "toastr"
import Breadcrumbs from "components/Common/Breadcrumb"

const PayrollSetup = () => {
    const [employeeList, setEmployeeList] = useState([])
    const [salaryGroups, setSalaryGroups] = useState([])
    const [components, setComponents] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [showSetupModal, setShowSetupModal] = useState(false)
    const [setupData, setSetupData] = useState({
        employee_id: "",
        employee_name: "",
        basic_salary: 0,
        salary_group: ""
    })

    const getEmployees = async () => {
        setIsLoading(true)
        const res = await get("api/employee?limit=100")
        if (res.status === 200) {
            setEmployeeList(res.response.rows || [])
        }
        setIsLoading(false)
    }

    const getSalaryGroups = async () => {
        const res = await get("api/hrms/payroll/groups")
        if (res.status === 200) {
            setSalaryGroups(res.response.rows || [])
        }
    }

    const getComponents = async () => {
        const res = await get("api/hrms/payroll/components?limit=100")
        if (res.status === 200) {
            setComponents(res.response.rows || [])
        }
    }

    useEffect(() => {
        getEmployees()
        getSalaryGroups()
        getComponents()
    }, [])

    const handleSalarySetupUpdate = async () => {
        const res = await post("api/hrms/payroll/salary_setup", setupData)
        if (res.status === 200 || res.status === true) {
            toastr.success("Salary setup updated successfully")
            setShowSetupModal(false)
            getEmployees()
        } else {
            toastr.error("Failed to update salary setup")
        }
    }

    const calculateTotals = (basic, groupId) => {
        const group = salaryGroups.find(g => g.id == groupId)
        if (!group || !group.component_ids) return { allowance: 0, deduction: 0 }

        const componentIds = group.component_ids.split(',')
        let allowance = 0
        let deduction = 0

        componentIds.forEach(id => {
            const comp = components.find(c => c.id == id)
            if (comp) {
                let amount = 0
                if (comp.value_type === 'Fixed') {
                    amount = parseFloat(comp.value)
                } else {
                    amount = (parseFloat(basic) * parseFloat(comp.value)) / 100
                }

                if (comp.type === 'Earning') allowance += amount
                else deduction += amount
            }
        })

        return { allowance, deduction }
    }

    const columns = [
        { dataField: "code", text: "EMP ID", sort: true },
        { dataField: "firstname", text: "NAME", sort: true, formatter: (cell, row) => `${row.firstname} ${row.lastname}` },
        { dataField: "designation", text: "DESIGNATION", sort: true },
        { dataField: "basic_salary", text: "BASIC SALARY", formatter: (cell) => `₹${cell || 0}` },
        { 
            dataField: "salary_group", 
            text: "SALARY GROUP",
            formatter: (cell) => salaryGroups.find(g => g.id == cell)?.name || "Not Assigned"
        },
        {
            dataField: "allowance",
            text: "ALLOWANCE",
            formatter: (cell, row) => {
                const { allowance } = calculateTotals(row.basic_salary, row.salary_group)
                return <span className="text-success">+₹{allowance.toFixed(2)}</span>
            }
        },
        {
            dataField: "deduction",
            text: "DEDUCTION",
            formatter: (cell, row) => {
                const { deduction } = calculateTotals(row.basic_salary, row.salary_group)
                return <span className="text-danger">-₹{deduction.toFixed(2)}</span>
            }
        },
        {
            dataField: "action",
            text: "ACTION",
            formatter: (cell, row) => (
                <Button color="light" size="sm" title="Edit Setup" onClick={() => {
                    setSetupData({
                        employee_id: row.code,
                        employee_name: `${row.firstname} ${row.lastname}`,
                        basic_salary: row.basic_salary || 0,
                        salary_group: row.salary_group || ""
                    })
                    setShowSetupModal(true)
                }}>
                    <i className="fas fa-pencil-alt text-primary"></i>
                </Button>
            )
        }
    ]

    const brownBtnStyle = { backgroundColor: "#3d1e08", borderColor: "#3d1e08", color: "#fff" }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="HRMS" breadcrumbItem="Payroll Setup" />
                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardBody>
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h4 className="card-title">Employee Salary Configurations</h4>
                                        <Button color="primary" onClick={getEmployees}>
                                            <i className="bx bx-refresh"></i> Refresh
                                        </Button>
                                    </div>
                                    <Datatables 
                                        rows={employeeList}
                                        columns={columns}
                                        keyField="code"
                                        loading={isLoading}
                                        isRemote={false}
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    <Modal isOpen={showSetupModal} toggle={() => setShowSetupModal(false)} centered>
                        <ModalHeader toggle={() => setShowSetupModal(false)}>Configure Employee Salary</ModalHeader>
                        <ModalBody>
                            <Form>
                                <FormGroup>
                                    <Label>Employee Name</Label>
                                    <Input value={setupData.employee_name} disabled />
                                </FormGroup>
                                <FormGroup>
                                    <Label>Monthly Basic Salary</Label>
                                    <Input 
                                        type="number"
                                        value={setupData.basic_salary} 
                                        onChange={(e) => setSetupData(prev => ({ ...prev, basic_salary: e.target.value }))}
                                        placeholder="Enter amount in ₹"
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label>Assign Salary Group</Label>
                                    <Select 
                                        options={salaryGroups.map(g => ({ value: g.id, label: g.name }))}
                                        value={salaryGroups.map(g => ({ value: g.id, label: g.name })).find(g => g.value == setupData.salary_group)}
                                        onChange={(e) => setSetupData(prev => ({ ...prev, salary_group: e.value }))}
                                        placeholder="Select components group..."
                                    />
                                </FormGroup>
                            </Form>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={() => setShowSetupModal(false)}>Cancel</Button>
                            <Button style={brownBtnStyle} onClick={handleSalarySetupUpdate}>Save Configuration</Button>
                        </ModalFooter>
                    </Modal>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default PayrollSetup
